/**
 * Sistema de fallback robusto para quando múltiplos sistemas falham
 */

import { NavigateFunction } from "react-router";
import { toast } from "react-toastify";

interface FallbackOptions {
  context: string;
  level: "graceful" | "partial" | "critical";
  retryable?: boolean;
  customMessage?: string;
  navigate?: NavigateFunction;
}

interface FallbackStrategy {
  name: string;
  description: string;
  execute: (options: FallbackOptions) => Promise<boolean> | boolean;
}

class SystemFallbackService {
  private strategies: FallbackStrategy[] = [];
  private failureHistory: Map<string, number[]> = new Map();
  private lastFallbackTime: number = 0;
  private readonly FALLBACK_COOLDOWN = 5000; // 5 segundos entre fallbacks

  constructor() {
    this.initializeStrategies();
  }

  private initializeStrategies() {
    // Estratégias ordenadas por preferência (menos invasiva para mais invasiva)
    this.strategies = [
      {
        name: "toast-notification",
        description: "Mostrar notificação toast simples",
        execute: (options) => this.showToastFallback(options),
      },
      {
        name: "local-storage-backup",
        description: "Tentar usar dados do localStorage como backup",
        execute: (options) => this.tryLocalStorageBackup(options),
      },
      {
        name: "offline-mode",
        description: "Ativar modo offline com funcionalidades limitadas",
        execute: (options) => this.activateOfflineMode(options),
      },
      {
        name: "route-to-safe-page",
        description: "Navegar para página segura",
        execute: (options) => this.routeToSafePage(options),
      },
      {
        name: "emergency-reload",
        description: "Recarregar a aplicação como último recurso",
        execute: (options) => this.emergencyReload(options),
      },
    ];
  }

  /**
   * Executa fallback baseado no nível de criticidade
   */
  async executeFallback(options: FallbackOptions): Promise<boolean> {
    // Evitar fallbacks muito frequentes
    const now = Date.now();
    if (now - this.lastFallbackTime < this.FALLBACK_COOLDOWN) {
      return false;
    }

    this.lastFallbackTime = now;
    this.recordFailure(options.context);

    // Determinar estratégias baseadas no nível
    const applicableStrategies = this.getApplicableStrategies(options.level);

    // Tentar cada estratégia até uma funcionar
    for (const strategy of applicableStrategies) {
      try {
        const success = await strategy.execute(options);
        if (success) {
          if (process.env.NODE_ENV === "development") {
            console.info(
              `✅ Fallback strategy '${strategy.name}' succeeded for context: ${options.context}`
            );
          }
          return true;
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn(
            `❌ Fallback strategy '${strategy.name}' failed:`,
            error
          );
        }
        continue;
      }
    }

    // Se todas as estratégias falharam, usar última opção
    return this.absoluteLastResort(options);
  }

  /**
   * Obtém contagem de falhas para um contexto
   */
  getFailureCount(context: string): number {
    const failures = this.failureHistory.get(context) || [];
    const fiveMinutesAgo = Date.now() - 300000;
    const recentFailures = failures.filter((time) => time > fiveMinutesAgo);
    return recentFailures.length;
  }

  /**
   * Verifica se um contexto está em estado crítico
   */
  isContextCritical(context: string): boolean {
    return this.getFailureCount(context) >= 5;
  }

  /**
   * Reset do histórico de falhas
   */
  resetFailureHistory(context?: string): void {
    if (context) {
      this.failureHistory.delete(context);
    } else {
      this.failureHistory.clear();
    }
  }

  private recordFailure(context: string): void {
    const failures = this.failureHistory.get(context) || [];
    failures.push(Date.now());

    // Manter apenas as últimas 10 falhas
    if (failures.length > 10) {
      failures.splice(0, failures.length - 10);
    }

    this.failureHistory.set(context, failures);
  }

  private getApplicableStrategies(
    level: FallbackOptions["level"]
  ): FallbackStrategy[] {
    switch (level) {
      case "graceful":
        return this.strategies.slice(0, 2); // Toast e localStorage
      case "partial":
        return this.strategies.slice(0, 4); // Inclui modo offline e navegação
      case "critical":
        return this.strategies; // Todas as estratégias
      default:
        return this.strategies.slice(0, 3);
    }
  }

  private showToastFallback(options: FallbackOptions): boolean {
    const message =
      options.customMessage || this.getDefaultMessage(options.level);

    try {
      toast.error(message, {
        toastId: `fallback-${options.context}`, // Evitar duplicatas
        autoClose: false,
        closeOnClick: false,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  private tryLocalStorageBackup(options: FallbackOptions): boolean {
    try {
      const backupData = localStorage.getItem(`backup-${options.context}`);
      if (backupData) {
        // Tentar restaurar dados do backup
        const data = JSON.parse(backupData);

        toast.info(
          "Usando dados salvos localmente devido a problemas de conectividade.",
          {
            autoClose: 5000,
          }
        );

        // Dispatch evento customizado para componentes reagirem
        window.dispatchEvent(
          new CustomEvent("fallback-data-restored", {
            detail: { context: options.context, data },
          })
        );

        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  private activateOfflineMode(options: FallbackOptions): boolean {
    try {
      // Ativar modo offline
      localStorage.setItem("offline-mode", "true");
      localStorage.setItem("offline-reason", options.context);

      toast.warning(
        "Modo offline ativado. Algumas funcionalidades podem estar limitadas.",
        {
          autoClose: false,
          closeButton: true,
        }
      );

      // Dispatch evento para componentes reagirem
      window.dispatchEvent(
        new CustomEvent("offline-mode-activated", {
          detail: { context: options.context, level: options.level },
        })
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  private routeToSafePage(options: FallbackOptions): boolean {
    if (!options.navigate) {
      return false;
    }

    try {
      // Determinar página segura baseada no contexto
      let safePage = "/";

      if (options.context.includes("auth")) {
        safePage = "/signin";
      } else if (options.context.includes("dashboard")) {
        safePage = "/";
      } else if (options.context.includes("payment")) {
        safePage = "/bikes";
      }

      options.navigate(safePage, {
        state: {
          fallback: true,
          originalContext: options.context,
          message: "Redirecionado devido a problemas técnicos.",
        },
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  private emergencyReload(options: FallbackOptions): boolean {
    try {
      // Salvar estado atual se possível
      const currentState = {
        url: window.location.href,
        timestamp: Date.now(),
        context: options.context,
        level: options.level,
      };

      sessionStorage.setItem(
        "emergency-reload-state",
        JSON.stringify(currentState)
      );

      // Mostrar mensagem antes de recarregar
      const message =
        "Recarregando a aplicação para resolver problemas técnicos...";

      // Usar alert se toast falhar
      try {
        toast.error(message, { autoClose: 2000 });
        setTimeout(() => window.location.reload(), 2000);
      } catch {
        alert(message);
        window.location.reload();
      }

      return true;
    } catch (error) {
      // Se tudo mais falhar, force reload
      window.location.reload();
      return true;
    }
  }

  private absoluteLastResort(options: FallbackOptions): boolean {
    try {
      // Se chegou aqui, nada mais funcionou
      const message = `Sistema enfrentando problemas críticos. Contexto: ${options.context}`;

      // Tentar qualquer método de notificação disponível
      if (window.confirm) {
        const userChoice = window.confirm(
          `${message}\n\nDeseja recarregar a página para tentar resolver?`
        );
        if (userChoice) {
          window.location.reload();
        }
      } else if (window.alert) {
        window.alert(message);
      } else {
        // Se nem alert funciona, log no console
        console.error("🚨 CRITICAL SYSTEM FAILURE:", message);
      }

      return true;
    } catch {
      // Absolutamente último recurso
      window.location.reload();
      return true;
    }
  }

  private getDefaultMessage(level: FallbackOptions["level"]): string {
    switch (level) {
      case "graceful":
        return "Ocorreu um problema menor. Tentando novamente...";
      case "partial":
        return "Sistema com problemas. Algumas funcionalidades podem estar limitadas.";
      case "critical":
        return "Sistema enfrentando problemas críticos. Por favor, recarregue a página.";
      default:
        return "Ocorreu um problema inesperado.";
    }
  }
}

// Instância singleton
export const systemFallback = new SystemFallbackService();

// Hook para usar em componentes React
export const useSystemFallback = () => {
  const executeFallback = (options: FallbackOptions) => {
    return systemFallback.executeFallback(options);
  };

  const getFailureCount = (context: string) => {
    return systemFallback.getFailureCount(context);
  };

  const isContextCritical = (context: string) => {
    return systemFallback.isContextCritical(context);
  };

  const resetFailures = (context?: string) => {
    systemFallback.resetFailureHistory(context);
  };

  return {
    executeFallback,
    getFailureCount,
    isContextCritical,
    resetFailures,
  };
};

// Função helper para executar fallback baseado em erro
export const executeFallbackFromError = async (
  error: any,
  context: string,
  navigate?: NavigateFunction
): Promise<boolean> => {
  let level: FallbackOptions["level"] = "graceful";

  // Determinar nível baseado no erro
  if (error?.response?.status >= 500 || !navigator.onLine) {
    level = "critical";
  } else if (error?.response?.status >= 400) {
    level = "partial";
  }

  const failureCount = systemFallback.getFailureCount(context);
  if (failureCount >= 3) {
    level = "critical";
  }

  return systemFallback.executeFallback({
    context,
    level,
    navigate,
    customMessage: error?.message,
  });
};

export default systemFallback;
